import type { NavSectionProps } from 'src/components/nav-section';

import Fuse from 'fuse.js';

import { flattenNavSections } from 'src/layouts/components/searchbar/utils';

import { searchItems } from './searchItems';

import type { ProviderSearchResult } from './providers/types';
import type { GlobalSearchItem , SearchResultSource } from './searchTypes';


export type CommandPaletteItem = {
  id: string;
  title: string;
  path: string;
  group: string;
  subtitle?: string;
  weight: number;
  fuseText: string;
  source: SearchResultSource;
  semanticScore?: number;
};

function globalToCommandItem(item: GlobalSearchItem): CommandPaletteItem {
  const group = item.subCategory ? `${item.category} · ${item.subCategory}` : item.category;
  const fuseText = [...(item.breadcrumb ?? []), ...(item.keywords ?? [])].join(' ');
  return {
    id: item.id,
    title: item.title,
    path: item.route,
    group,
    subtitle: item.subtitle,
    weight: item.weight ?? 5,
    fuseText,
    source: 'navigation',
  };
}

function navToCommandItem(item: {
  title: string;
  path: string;
  group: string;
}): CommandPaletteItem {
  return {
    id: `${item.path}:${item.title}`,
    title: item.title,
    path: item.path,
    group: item.group,
    weight: 5,
    fuseText: '',
    source: 'navigation',
  };
}

export function buildCommandPaletteItems(navData: NavSectionProps['data']): CommandPaletteItem[] {
  const global = searchItems.map(globalToCommandItem);
  const byPath = new Map<string, CommandPaletteItem>();

  for (const n of flattenNavSections(navData)) {
    byPath.set(n.path, navToCommandItem(n));
  }
  for (const g of global) {
    byPath.set(g.path, g);
  }

  return Array.from(byPath.values());
}

export function filterCommandPaletteItems(
  items: CommandPaletteItem[],
  query: string
): CommandPaletteItem[] {
  const trimmed = query.trim();
  const sortedDefault = [...items].sort((a, b) => {
    if (b.weight !== a.weight) return b.weight - a.weight;
    return a.title.localeCompare(b.title);
  });

  if (!trimmed) {
    return sortedDefault;
  }

  const fuse = new Fuse(items, {
    keys: [
      { name: 'title', weight: 0.45 },
      { name: 'subtitle', weight: 0.2 },
      { name: 'path', weight: 0.25 },
      { name: 'group', weight: 0.15 },
      { name: 'fuseText', weight: 0.35 },
    ],
    threshold: 0.42,
    ignoreLocation: true,
    includeScore: true,
  });

  const results = fuse.search(trimmed);

  return results
    .sort((a, b) => {
      if (b.item.weight !== a.item.weight) return b.item.weight - a.item.weight;
      return (a.score ?? 0) - (b.score ?? 0);
    })
    .map((r) => r.item);
}

export function providerSearchResultToCommandItem(
  item: ProviderSearchResult
): CommandPaletteItem {
  return {
    id: item.id,
    title: item.title,
    path: item.path,
    group: item.group,
    subtitle: item.subtitle,
    weight: item.weight ?? 8,
    fuseText: item.fuseText ?? '',
    source: item.source,
    semanticScore: item.score,
  };
}

export function mergeCommandPaletteItems(
  baseItems: CommandPaletteItem[],
  semanticItems: ProviderSearchResult[]
): CommandPaletteItem[] {
  const byIdentity = new Map<string, CommandPaletteItem>();

  for (const item of baseItems) {
    byIdentity.set(`${item.path}::${item.title}`, item);
  }

  for (const item of semanticItems) {
    byIdentity.set(`${item.path}::${item.title}`, providerSearchResultToCommandItem(item));
  }

  return Array.from(byIdentity.values());
}
